import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getClientAccountData } from '../services/portalAccessService';

/** First non-empty trimmed string from candidates */
export const pickProfileValue = (...candidates) => {
  for (const v of candidates) {
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
};

export const BUSINESS_STRUCTURE_OPTIONS = [
  'Sole Proprietorship',
  'Partnership',
  'Limited Liability Company',
  'S Corporation',
  'C Corporation',
];

/** Map common abbreviations to full business structure labels */
export const normalizeBusinessStructure = (raw) => {
  if (!raw || !String(raw).trim()) return '';
  const key = String(raw).trim().toLowerCase();
  const aliases = {
    llc: 'Limited Liability Company',
    'l.l.c.': 'Limited Liability Company',
    'limited liability company (llc)': 'Limited Liability Company',
    's-corp': 'S Corporation',
    's corp': 'S Corporation',
    's corporation': 'S Corporation',
    'c-corp': 'C Corporation',
    'c corp': 'C Corporation',
    'c corporation': 'C Corporation',
    corp: 'C Corporation',
    corporation: 'C Corporation',
  };
  if (aliases[key]) return aliases[key];
  const match = BUSINESS_STRUCTURE_OPTIONS.find((opt) => opt.toLowerCase() === key);
  return match || String(raw).trim();
};

export const SIGNER_FORM_FIELDS = [
  { key: 'signerName', label: 'Full Name' },
  { key: 'signerTitle', label: 'Title' },
  { key: 'companyName', label: 'Company Name' },
  { key: 'businessStructure', label: 'Business Structure' },
  { key: 'companyAddress', label: 'Street' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipCode', label: 'Zip Code' },
];

/**
 * Load signer/company fields from user profile, account, and prior NDA data.
 */
export async function buildSignerFormDataFromProfile(user) {
  const userSnap = await getDoc(doc(db, 'users', user.uid));
  const userData = userSnap.exists() ? userSnap.data() : {};
  const accountData = await getClientAccountData(user.uid);
  const priorNda = userData.signedNDA || accountData?.signedNDA || null;
  const contact = accountData?.contact || {};
  const discovery = accountData?.discovery || {};
  // Values the signer typed at checkout when their profile was incomplete. Most recent
  // and self-corrected, so they outrank everything else.
  const typed = userData.signerProfile || {};

  const firstName = pickProfileValue(userData.firstName, contact.firstName);
  const lastName = pickProfileValue(userData.lastName, contact.lastName);
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return {
    signerName: pickProfileValue(
      typed.signerName,
      priorNda?.signerName,
      contact.name,
      fullName,
      user.displayName
    ),
    signerTitle: pickProfileValue(
      typed.signerTitle,
      priorNda?.signerTitle,
      userData.jobTitle,
      userData.title,
      contact.title
    ),
    companyName: pickProfileValue(
      typed.companyName,
      priorNda?.companyName,
      userData.company,
      accountData?.company,
      accountData?.companyName,
      discovery.companyName
    ),
    companyAddress: pickProfileValue(
      typed.companyAddress,
      priorNda?.companyAddress,
      userData.address?.street,
      accountData?.streetAddress,
      accountData?.location,
      discovery.streetAddress
    ),
    city: pickProfileValue(
      typed.city,
      priorNda?.city,
      userData.address?.city,
      accountData?.city,
      discovery.city
    ),
    state: pickProfileValue(
      typed.state,
      priorNda?.state,
      userData.address?.state,
      accountData?.state,
      discovery.state
    ),
    zipCode: pickProfileValue(
      typed.zipCode,
      priorNda?.zipCode,
      userData.address?.zipCode,
      accountData?.zipCode,
      discovery.zipCode
    ),
    businessStructure: normalizeBusinessStructure(
      pickProfileValue(
        typed.businessStructure,
        priorNda?.businessStructure,
        accountData?.businessStructure,
        discovery.businessStructure,
        userData.businessStructure
      )
    ),
  };
}

/** Return list of missing field labels for required signer fields */
export function getMissingSignerFields(formData) {
  return SIGNER_FORM_FIELDS.filter(({ key }) => {
    const value = formData?.[key];
    return value == null || (typeof value === 'string' && !value.trim());
  }).map(({ label }) => label);
}

export function isSignerFormComplete(formData) {
  return getMissingSignerFields(formData).length === 0;
}
